import type { TranslationFunction } from '@/app/components/translations/Translation.tsx';
import { useTranslation } from '@/app/components/translations/Translation.tsx';
import React, { useEffect, useRef, useState } from 'react';
import type { SearchModes } from '@/domainLogic/index.ts';
import type { DESTINATION_TYPE } from '@/app/search/types.ts';
import getSearchParams from '@/app/components/search/common/getSearchParams.ts';
import { getSearchModeFromSearchParams } from '@/app/components/search/utils/query2.ts';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { createSortOptions } from '@/app/common/sort/results/createSortOptions.ts';
import type { CustomPillElementProps, FilterPill } from '@/app/common/filters/surfaces/bar/pills/types.ts';
import Pill from '@/app/common/components/Pill';
import FilterGroupOverlay, {
  SORT_OVERLAY_ID,
} from '@/app/common/filters/surfaces/bar/pills/overlay/FilterGroupOverlay.tsx';
import { useCommonClient } from '@/app/common/commonClientProvider.tsx';
import { useIsModalViewport } from '@/app/components/search/utils/useSsrMedia.ts';
import { MODAL_SIZES } from '@/app/components/search/components/Modal/OpenXModal/types.ts';
import OpenXModal from '@/app/components/search/components/Modal/OpenXModal';
import ModalHeader from '@/app/components/search/components/Modal/LayoutElements/ModalHeader';
import type { HandleFilterChanged } from '@/app/common/filters/core/filterComponentType.ts';
import type { Filter } from '@/app/common/filters/core/filtersTypes.ts';
import { FILTER_TYPE } from '@/app/common/filters/core/filtersTypes.ts';
import { FilterRadio } from '@/app/common/filters/types/radio/FilterRadio.tsx';
import { FILTER_NAME } from '@/app/common/filters/core/filterNames.ts';
import RankingBox from '@/app/components/search/components/Title/RankingBox';
import styled from 'styled-components';
import { Separator } from '@/app/common/filters/surfaces/styled.ts';
import { useReopenGuard } from '@/app/common/hooks/useReopenGuard.ts';

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ApplyButton = styled.button`
  width: 100%;
  height: 48px;
  background-color: ${({ theme }) => theme.kallisto.color.background.static.backgroundBrandPrimary};
  color: #fff;
  font-size: ${({ theme }) => theme.kallisto.font.size['175']};
  font-weight: ${({ theme }) => theme.kallisto.font.weight.bold};
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

export const SORT_MOBILE_MODAL_ID = 'sort-mobile-modal';

type SortDetailsProps = {
  searchMode: SearchModes;
  isLoading: boolean;
  destinationType?: DESTINATION_TYPE;
  handleFilterChanged: HandleFilterChanged;
  t: TranslationFunction;
};

const buildSortFilter = (sortOptions: ReturnType<typeof createSortOptions>, isLoading: boolean): Filter => ({
  name: FILTER_NAME.SORT,
  type: FILTER_TYPE.RADIO,
  disabled: isLoading,
  data: {
    options: sortOptions.map((option) => ({
      label: option.label,
      selected: option.isSelected,
      counter: undefined,
      value: option.value,
    })),
  },
});

type MobileSortModalProps = Omit<SortDetailsProps, 'handleFilterChanged'> & {
  isOpen: boolean;
  onClose: () => void;
  handleFilterChanged?: HandleFilterChanged;
};

const MobileSortModal = ({
  isOpen,
  onClose,
  searchMode,
  isLoading,
  destinationType,
  t,
  handleFilterChanged,
}: MobileSortModalProps) => {
  const { localization } = usePageWrapperData();
  const { brand } = localization;
  const isEvo = localization?.configValuesApp?.evolutionResultsOnlyEnabled;
  const sortOptions = createSortOptions({ searchMode, destinationType, t });
  const { isMobile } = useCommonClient();

  const [pendingFilter, setPendingFilter] = useState<Filter>(() => buildSortFilter(sortOptions, isLoading));

  useEffect(() => {
    if (isOpen) setPendingFilter(buildSortFilter(sortOptions, isLoading));
  }, [isOpen, isLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleApply = () => {
    if (handleFilterChanged) handleFilterChanged([pendingFilter]);
    onClose();
  };

  return (
    <OpenXModal
      id={SORT_MOBILE_MODAL_ID}
      isMobile={isMobile}
      isOpen={isOpen}
      onClose={onClose}
      brand={brand}
      size={MODAL_SIZES.XXL}
      scrollShadows={isEvo}
      bodyBackgroundColor="white"
      header={{
        sticky: true,
        element: (
          <ModalHeader handleClose={onClose}>{t('vacation.oss.components.searchresults.sort.sortby')}</ModalHeader>
        ),
      }}
      footer={{
        sticky: true,
        element: <ApplyButton onClick={handleApply}>{t('vacation.oss.filters.apply')}</ApplyButton>,
      }}
    >
      <Content>
        <FilterRadio
          filter={pendingFilter}
          isLoading={isLoading}
          handleFilterChanged={([updated]) => setPendingFilter(updated)}
          useStandardRadios={true}
        />
        {localization.rankingLink && (
          <>
            {!isMobile && <Separator />}
            <RankingBox localization={localization} variant="evolution" />
          </>
        )}
      </Content>
    </OpenXModal>
  );
};

const appliedSortOption = (filterPill?: FilterPill) => {
  const [sortFilter] = filterPill?.filters ?? [];
  if (!sortFilter || !('options' in sortFilter.data)) return undefined;

  return (sortFilter.data.options ?? []).find((option) => option.selected && !option.isDefault);
};

export const SortPill = ({ isLoading, busy, handleFilterChanged, filterPill, useModal }: CustomPillElementProps) => {
  const { isMobile, isDesktop } = useCommonClient();
  const isModalViewport = useIsModalViewport(isMobile);
  const { localization } = usePageWrapperData();
  const isEvo = localization?.configValuesApp?.evolutionResultsOnlyEnabled;
  const pillRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const searchParamsFresh = getSearchParams();
  const searchMode = getSearchModeFromSearchParams(searchParamsFresh);
  const sortOptions = createSortOptions({ searchMode, t });
  const [defaultOption] = sortOptions;
  const appliedOption = appliedSortOption(filterPill);
  const selectedOption = appliedOption ?? sortOptions.find((option) => option.isSelected);
  const isSortActive = !!appliedOption || (!!selectedOption && selectedOption !== defaultOption);
  const isCenteredModal = !!useModal && !isModalViewport;
  const applyOnChange = isDesktop || isCenteredModal;

  const [isOpen, setIsOpen] = useState(false);
  const disabled = isLoading || !!busy;
  const useMobileSortModal = !isEvo && !isDesktop;
  const overlayId = useMobileSortModal ? SORT_MOBILE_MODAL_ID : SORT_OVERLAY_ID;
  const [pendingFilter, setPendingFilter] = useState<Filter>(() => buildSortFilter(sortOptions, disabled));
  const reopenGuard = useReopenGuard();

  useEffect(() => {
    if (isOpen) setPendingFilter(buildSortFilter(sortOptions, disabled));
  }, [isOpen, disabled]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleOnClick = () => {
    if (reopenGuard.blocked()) return;
    setIsOpen(!isOpen);
  };

  const handleOnClose = () => {
    reopenGuard.markClosed();
    setIsOpen(false);
  };

  const handleRadioChanged: HandleFilterChanged = (filters) => {
    if (applyOnChange) {
      if (handleFilterChanged) handleFilterChanged(filters);
      handleOnClose();
      return;
    }
    const [updated] = filters;
    if (updated) setPendingFilter(updated);
  };

  const handleApply = () => {
    if (handleFilterChanged) handleFilterChanged([pendingFilter]);
    handleOnClose();
  };

  const label =
    selectedOption && isSortActive ? selectedOption.label : t('vacation.oss.components.searchresults.sort.title');

  const details = (
    <Content>
      <FilterRadio
        filter={applyOnChange ? buildSortFilter(sortOptions, disabled) : pendingFilter}
        isLoading={disabled}
        handleFilterChanged={handleRadioChanged}
        useStandardRadios={!applyOnChange}
      />
      {localization.rankingLink && (
        <>
          {!isMobile && <Separator />}
          <RankingBox localization={localization} variant="evolution" />
        </>
      )}
    </Content>
  );

  const overlay = useMobileSortModal ? (
    <MobileSortModal
      isOpen={isOpen}
      onClose={handleOnClose}
      searchMode={searchMode}
      t={t}
      handleFilterChanged={handleFilterChanged}
      isLoading={disabled}
    />
  ) : (
    <FilterGroupOverlay
      isOpen={isOpen}
      setIsOpen={(value) => (value ? setIsOpen(true) : handleOnClose())}
      isLoading={disabled}
      isModalViewport={isModalViewport}
      forceModal={useModal}
      pillRef={pillRef}
      filterPillFiltersComponents={details}
      columns={1}
      overlayId={overlayId}
      overlayTitle={filterPill?.label || t('vacation.oss.components.searchresults.sort.sortby')}
      handleApply={applyOnChange ? undefined : handleApply}
    />
  );

  return (
    <>
      <Pill
        isLoading={isLoading}
        busy={busy}
        label={label}
        icon={filterPill?.icon}
        handleClick={handleOnClick}
        hasPopover={true}
        showChevron={!isDesktop && !isSortActive}
        selected={isSortActive}
        isOpen={isOpen}
        ref={pillRef}
        controls={isOpen ? overlayId : undefined}
      />
      {overlay}
    </>
  );
};
